import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

const isSuccessfulStatus = (status) => status >= 200 && status < 300;

const LoginComponent = ({ setUser }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const { setAccessToken, setRefreshToken, setIsAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const theme = useTheme();

  const baseURL = process.env.REACT_APP_BASEURL;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${baseURL}/user-login/`, {
        username,
        password,
      });
      const { sso_token } = res.data;

      if (isSuccessfulStatus(res.status) && sso_token) {
        const tokenResponse = await axios.post(
          `${baseURL}/user-login/sso`,
          { sso_token }
        );
        if (!isSuccessfulStatus(tokenResponse.status)) {
          throw new Error(`SSO token exchange failed with status ${tokenResponse.status}`);
        }
        const { access, refresh } = tokenResponse.data;

        setAccessToken(access);
        setRefreshToken(refresh);
        setIsAuthenticated(true);

        localStorage.setItem('accessToken', access);
        localStorage.setItem('refreshToken', refresh);
        setLoading(false);
        navigate("/dashboard");
      } else {
        console.error("Unexpected response:", res.data);
        alert("Login failed: Invalid response");
        setLoading(false);
      }
    } catch (error) {
      console.error("Login error:", error);
      alert(
        "Login failed: " + (error.response?.data?.message || error.message)
      );
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh", // Make the background cover the whole viewport
        width: "100%",
        height: "100%",
        bgcolor: "#000000", // Black body background
      }}
    >
      <Box
        component="form"
        onSubmit={handleLogin}
        sx={{
          p: 4,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: theme.palette.primary.main, // Yellow form background
          width: '100%',
          maxWidth: 350,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h5" component="h2" align="center" gutterBottom sx={{ color: theme.palette.sidebar.main }}>
          Login
        </Typography>
        <TextField
          variant="outlined"
          label="Username"
          fullWidth
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          InputProps={{
            style: { color: theme.palette.text.primary },
          }}
          InputLabelProps={{
            style: { color: theme.palette.text.primary },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 5,
              backgroundColor: '#f5c518',
              '& fieldset': {
                color: theme.palette.text.primary,
                borderColor: theme.palette.text.primary, // Black border
                borderRadius: 5,
              },
              '&:hover fieldset': {
                border: 3,
                borderColor: theme.palette.text.white,
                borderRadius: 5,
              },
              '&.Mui-focused fieldset': {
                borderRadius: 5,
                borderColor: theme.palette.text.white,
                color: theme.palette.text.white,
              },
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 100px #f5c518 inset !important',
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                caretColor: theme.palette.text.primary,
              },
            },
          }}
        />
        <TextField
          variant="outlined"
          label="Password"
          type="password"
          fullWidth
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          InputProps={{
            style: { color: theme.palette.text.primary },
          }}
          InputLabelProps={{
            style: { color: theme.palette.text.primary },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 5,
              backgroundColor: '#f5c518',
              '& fieldset': {
                color: theme.palette.text.primary,
                borderColor: theme.palette.text.primary, // Black border
                borderRadius: 5,
              },
              '&:hover fieldset': {
                border: 3,
                borderColor: theme.palette.text.white,
                borderRadius: 5,
              },
              '&.Mui-focused fieldset': {
                borderRadius: 5,
                borderColor: theme.palette.text.white,
                color: theme.palette.text.white,
              },
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 100px #f5c518 inset !important',
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
                caretColor: theme.palette.text.primary,
              },
            },
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{
            py: 1.5,
            borderRadius: 5,
            width: 150,
            margin: "0 auto",
            fontWeight: 600,
            color: theme.palette.primary.main, // Yellow login text
            backgroundColor: theme.palette.sidebar.main,
            '&:hover': {
              backgroundColor: theme.palette.sidebar.main,
              color: theme.palette.text.secondary,
              transform: 'scale(1.03)',
            },
            '&:active': {
              transform: 'scale(0.97)',
              boxShadow: '0 0 10px rgba(245, 197, 24, 0.7)'
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="primary" /> : "Login"}
        </Button>
      </Box>
    </Box>
  );
};

export default LoginComponent;
